import * as React from 'react'
import { useId } from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'

import { cn } from '@/utils/common'

import { Check } from '../Icons'

const CheckboxRoot = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'grid place-content-center cursor-pointer peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('grid place-content-center text-current')}
    >
      <Check className="w-4 h-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
CheckboxRoot.displayName = CheckboxPrimitive.Root.displayName

export interface CheckboxProps extends Omit<
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  'checked' | 'onCheckedChange' | 'onChange'
> {
  label?: string | React.ReactNode
  labelClassName?: string
  checkBoxClassName?: string
  checked?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  name?: string
  id?: string
}

/**
 * Checkbox Component
 *
 * A flexible checkbox component built on Radix UI with support for labels,
 * indeterminate state, and full accessibility.
 *
 * Key features:
 * - Built on @radix-ui/react-checkbox for accessibility
 * - Optional label with configurable positioning
 * - Support for indeterminate state
 * - Disabled state handling
 * - Custom onChange handler compatible with React forms
 *
 * @param label - Optional text or ReactNode to display next to checkbox
 * @param checked - Controlled checked state
 * @param onChange - Event handler that receives React.ChangeEvent
 * @param disabled - Disable checkbox interaction
 * @param name - Name attribute for form submissions
 * @param id - Custom ID, auto-generated if not provided
 * @param className - Additional CSS classes
 *
 * @example
 * ```tsx
 * <Checkbox label="Remember me?" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
 *
 * <Checkbox disabled label="Disabled option" />
 * ```
 */
const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked = false,
  onChange,
  disabled = false,
  name,
  className = '',
  labelClassName = '',
  checkBoxClassName = '',
  id,
  ...rest
}) => {
  const generatedId = useId()
  const sanitizedId = generatedId.replace(/:/g, '') //NOSONAR - remove colons from generated id
  const inputId = id ?? `checkbox-${name ?? sanitizedId}`

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'inline-flex items-center gap-2',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className
      )}
    >
      <CheckboxRoot
        id={inputId}
        checked={checked}
        onCheckedChange={(isChecked) =>
          onChange?.({
            target: {
              checked: isChecked === true,
              name,
              type: 'checkbox',
            } as HTMLInputElement,
          } as React.ChangeEvent<HTMLInputElement>)
        }
        disabled={disabled}
        className={cn(
          'w-5 h-5 border border-solid border-[--Colors-Gray-light-300] bg-transparent rounded-[4px] data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600',
          checkBoxClassName
        )}
        {...rest}
      />
      {label && (
        <span
          className={cn(
            'text-sm',
            disabled ? 'text-gray-400' : 'text-gray-700',
            labelClassName
          )}
        >
          {label}
        </span>
      )}
    </label>
  )
}

export { Checkbox }
